import Card from '@src/shared/components/Card';
import styles from './styles';
import React, { FC, useEffect } from 'react';
import { IOrder, useOrdersActions, useOrdersState } from '../../context/orders';
import { OrderStatus } from '@pages/Deliveries/context/orders';
import InformationBadges from '@src/shared/components/InformationBadges';
import { useTranslation } from 'react-i18next';
import { IconButton, Typography, useTheme } from '@material-ui/core';
import JetFontIcon, {
  JET_FONTICON_ALIASES,
} from '@src/shared/components/JetFontIcon';
import { useCouriersState } from '../../context/couriers';
import VehicleTypeIcon from '../VehicleTypeIcon';
import { getTimeFromDate, parseGuestName } from '@src/shared/utils';
import Note, { NoteType } from '@src/shared/components/Note';
import AccordionWrapper from '@src/shared/components/Accordion';
import OrderAssignAccordion from '../OrderAssignAccordion';
import { useFeatureFlaggingState } from '@src/shared/context/featureflaging';
import useHover from '@src/shared/hooks/use-hover';
import {
  useAccordionActions,
  useAccordionState,
} from '@src/shared/context/accordion';
import {
  DrawerType,
  useInsideDrawerActions,
  useInsideDrawerState,
} from '@src/shared/context/inside-drawer';
import { trackDeliveryDashboardOrderCardClick } from '../../tracking/delivery-dashboard';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import i18n from '@src/i18n';

interface OrderCardItem {
  cardItems: Partial<OrderListItemProps>;
}

interface OrderListItemProps extends IOrder {
  sequence?: number;
  isTestRestaurant?: boolean;
}


export const OrderCard: FC<Partial<OrderCardItem>> = ({ cardItems }) => {
  const classes = styles();
  const { t } = useTranslation();
  const theme = useTheme();
  const { couriersById } = useCouriersState();
  const { isTestRestaurant } = useFeatureFlaggingState();
  const [isHover, elementRef] = useHover();
  const { setHoveredOrder, flushHoveredOrder } = useAccordionActions();
  const { hoveredOrderReference } = useAccordionState();
  const { setActiveInsideDrawer,setDrawerFlush} = useInsideDrawerActions();
  const {activeDrawerState,closeOnClick} = useInsideDrawerState();
  const { setClickedOrderId } = useOrdersActions();
  const {clickedOrderId} = useOrdersState();
  const {restaurant} = useKeycloakUserState()

  const getOrderStatusTag = (orderStatus: OrderStatus) => {
    if (orderStatus === undefined) {
      return OrderStatus.Unassigned;
    }
    if (orderStatus === OrderStatus.AssignedToNextPool) {
      return OrderStatus.Assigned;
    }
    return orderStatus;
  };

  const handleDrawerState = (orderId:string) => {
    if(activeDrawerState === DrawerType.ORDER_CARD) {
      if(orderId === clickedOrderId) {
        trackDeliveryDashboardOrderCardClick({
          language: i18n.language,
          country: restaurant?.country as string,
          order_alcohol: cardItems?.hasAlcohol as boolean,
          order_action: 'single order detail close',
          order_hashed: cardItems?.hash as string,
          order_id: cardItems?.reference as string
        })
        setDrawerFlush()
      } else {
        return null;
      }
    } else {
      trackDeliveryDashboardOrderCardClick({
        language: i18n.language,
        country: restaurant?.country as string,
        order_alcohol: cardItems?.hasAlcohol as boolean,
        order_action: 'single order detail open',
        order_hashed: cardItems?.hash as string,
        order_id: cardItems?.reference as string
      })
      setActiveInsideDrawer(DrawerType.ORDER_CARD);
    }
    setClickedOrderId(orderId)
  }

  useEffect(() => {
    if (isHover && hoveredOrderReference !== cardItems?.uuid) {
      if(process.env.NODE_ENV === 'development') {
      console.log({
        reference: `https://jetcourier.page.link/qr/?ref=${cardItems?.reference}&id=${cardItems?.hash}`,
      });
    }
      setHoveredOrder(cardItems?.uuid as string);
    } else {
      flushHoveredOrder();
    }
  }, [isHover]);

  useEffect(() => {
    if(closeOnClick) {
      if(cardItems?.uuid === clickedOrderId) {
        trackDeliveryDashboardOrderCardClick({
          language: i18n.language,
          country: restaurant?.country as string,
          order_alcohol: cardItems?.hasAlcohol as boolean,
          order_action: 'single order detail close',
          order_hashed: cardItems?.hash as string,
          order_id: cardItems?.reference as string
        })
      }   
    }
  },[closeOnClick])
  
  return (
    <div className={`${classes.orderCardContent}`}  ref={elementRef as any}>
      <Card  isCardActive={clickedOrderId === cardItems?.uuid && activeDrawerState === DrawerType.ORDER_CARD ? true : false}>
        <Card.Body>
          <div className={classes.orderCardBody}>
            <div className={classes.orderCardBodyLeft}>
              <JetFontIcon
                bold
                color={theme.palette.jet?.contentBrand}
                iconname={JET_FONTICON_ALIASES.HOUSE}
                size={12}
              />
              <div className={classes.orderCardBodyLeftContent}>
                <Typography variant="body1" className="body_bold elipsis">
                  {cardItems?.customer?.address?.street}{' '}
                  {cardItems?.customer?.address?.streetNumber}
                </Typography>
                <Typography variant="body1" className="body_small_regular ">
                  {cardItems?.customer?.address?.postcode}{' '}
                  {cardItems?.customer?.address?.city}
                </Typography>
              </div>
            </div>
            <div className={classes.orderCardBodyRight}>
              <div className={classes.orderCardBodyRightTime}>
                {cardItems?.status !== OrderStatus.Delivered ? (
                  <Typography variant="body1" className="body_regular">
                    ETA
                  </Typography>
                ) : null}
                <Typography variant="body1" className="body_bold">
                  {cardItems?.expectedDeliveryTime ? (
                    getTimeFromDate(
                      cardItems?.status === OrderStatus.Delivered
                        ? cardItems?.updateDate
                        : (cardItems.expectedDeliveryTime as any)
                    )
                  ) : (
                    <>
                      {cardItems?.status === OrderStatus.Delivered &&
                      cardItems?.updateDate
                        ? getTimeFromDate(cardItems?.updateDate)
                        : 'TBA'}
                    </>
                  )}
                </Typography>
              </div>
            </div>
          </div>
          <div className={classes.orderCardSub}>
            {cardItems?.hasAlcohol ? (
              <div className={classes.orderCardSubNote}>
                <Note
                  note="order.alcohol"
                  icon={{
                    name: JET_FONTICON_ALIASES.ALCOHOL,
                    color: theme?.palette?.jet?.contentDefault as string,
                    size: 12,
                  }}
                  type={NoteType.Warning}
                />
              </div>
            ) : null}

            <div className={classes.orderCardSubRightClient}>
              <JetFontIcon
                color={theme.palette.jet?.contentBrand}
                iconname={JET_FONTICON_ALIASES.USER}
                size={12}
              />
              <Typography variant="body1" className="body_small_bold ">
                {cardItems?.customer?.lastName}
              </Typography>
            </div>
          </div>
        </Card.Body>
        <Card.Footer>
          <div className={classes.orderCardFooter}>
            {cardItems?.courier &&
            cardItems.status !== OrderStatus.Unassigned ? (
              <>
                <div className={classes.orderCardCourierInfo}>
                  <VehicleTypeIcon
                    iconType="BADGE"
                    iconSize={12}
                    iconColor={theme.palette.jet?.contentBrand}
                    vehicleType={
                      cardItems?.status === OrderStatus.Delivered
                        ? cardItems?.courier.vehicleType
                        : couriersById[cardItems?.courier?.id]?.vehicleType
                    }
                  />
                  {couriersById[cardItems?.courier?.id]?.isGuest && <Typography variant="body1" className="body_small_bold ">
                    {parseGuestName(couriersById[cardItems?.courier?.id]?.firstName)}
                  </Typography>}
                  {!couriersById[cardItems?.courier?.id]?.isGuest && <Typography variant="body1" className="body_small_bold ">
                    {cardItems?.courier?.firstName ||
                      cardItems?.courier?.lastName}
                  </Typography>}
                </div>
                <div className={classes.orderCardDetailWrapper}>
                  <InformationBadges
                    flag={getOrderStatusTag(cardItems.status as OrderStatus)}
                    single
                    label={t(
                      `order.status_${getOrderStatusTag(
                        cardItems.status as OrderStatus
                      )}`
                    )}
                  />
                  <IconButton
                    classes={{ root: 'details-btn' }}
                    size="medium"
                    aria-label="See details"
                    onClick={() => handleDrawerState(cardItems?.uuid as string)}
                  >
                    <JetFontIcon
                      color={theme.palette.jet?.supportBrand01}
                      iconname={clickedOrderId === cardItems?.uuid && activeDrawerState === DrawerType.ORDER_CARD ? JET_FONTICON_ALIASES.CLOSE : JET_FONTICON_ALIASES.CHEVRON_RIGHT}
                      size={clickedOrderId === cardItems?.uuid && activeDrawerState === DrawerType.ORDER_CARD ? 12 : 16}
                    />
                  </IconButton>
                </div>
              </>
            ) : (
              <>
                <JetFontIcon
                  bold
                  color={theme.palette.jet?.borderStrong}
                  iconname={JET_FONTICON_ALIASES.BIKE_SMALL}
                  size={12}
                />
                <div className={classes.orderCardDetailWrapper}>
                  <InformationBadges
                    flag={getOrderStatusTag(cardItems?.status as OrderStatus)}
                    single
                    label={t(
                      `order.status_${getOrderStatusTag(
                        cardItems?.status as OrderStatus
                      )}`
                    )}
                  />
                  <IconButton
                    classes={{ root: 'details-btn' }}
                    size="medium"
                    aria-label="See details"
                    onClick={() =>  handleDrawerState(cardItems?.uuid as string)}
                  >
                    <JetFontIcon
                      color={theme.palette.jet?.supportBrand01}
                      iconname={clickedOrderId === cardItems?.uuid && activeDrawerState === DrawerType.ORDER_CARD ? JET_FONTICON_ALIASES.CLOSE : JET_FONTICON_ALIASES.CHEVRON_RIGHT}
                      size={clickedOrderId === cardItems?.uuid && activeDrawerState === DrawerType.ORDER_CARD  ? 12 : 16}
                    />
                  </IconButton>
                </div>
              </>
            )}
          </div>
        </Card.Footer>
        {isTestRestaurant ? (
          <>
            {cardItems?.status === OrderStatus.Unassigned ||
            cardItems?.status === OrderStatus.Assigned ||
            cardItems?.status === OrderStatus.AssignedToNextPool ? (
              <Card.Menu>
                <AccordionWrapper
                  summaryStatus={cardItems.status}
                  accordionReference={cardItems.reference as string}
                  summaryData={{
                    hasAlcohol: cardItems.hasAlcohol,
                    hash: cardItems.hash,
                  }}
                >
                  {Object.keys(couriersById).length > 0 ? (
                    <>
                      <OrderAssignAccordion
                        couriers={couriersById}
                        courierItems={{ ...cardItems }}
                      />
                    </>
                  ) : (
                    <Typography
                      variant="body1"
                      className={`body_small_bold ${classes.itemNoData}`}
                    >
                      {t('order.accordion_no_courier')}
                    </Typography>
                  )}
                </AccordionWrapper>
              </Card.Menu>
            ) : null}
          </>
        ) : null}
      </Card>
    </div>
  );
};
